import React, { useState, useEffect, useCallback, useRef } from "react";
import StyledFooter from "components/StyledFooter";
import { Container as MuiContainer, Box, Toolbar } from "@mui/material";

import { Routes, Route } from "react-router-dom";
import { LoginCallback } from "@okta/okta-react";
import ProtectedRoute from "components/ProtectedRoute";
import Navbar from "components/Navbar/index";
// Containers
import PolicyEnforcementRulesView from "containers/PolicyEnforcementRules/PolicyEnforcementRulesView";
import PolicyHistory from "containers/PolicyHistory";
import PolicyTranslation from "containers/PolicyTranslation";
import HealthPlanConfiguration from "containers/HPConfiguration";
import EditHealthPlanConfiguration from "containers/HPConfiguration/EditHealthPlanConfiguration";
import PolicyConfiguration from "containers/PolicyConfiguration";
import CreatePolicyConfig from "containers/PolicyConfiguration/CreatePolicy";
import UseCases from "containers/UseCases";
import Home from "containers/Home";
import PolicyCoding from "containers/PolicyCoding";
import PolicyCodingPostOHR from "containers/PolicyCodingPostOHR";
import Exclusions from "containers/Exclusions";
import { useUser, useAuthentication } from "hooks/useUser";
import { FeatureFlagContext, UserContext } from "context";
import LoadingBackdrop from "components/LoadingBackDrop";
import useIdleTimeout from "hooks/useIdleTimer";
import IdleModal from "components/IdleModal";
import GenericDialog from "components/GenericDialog";
import { useSelector } from "react-redux";
import { useHealthcheck } from "hooks/useHealthcheck";
import useFeatureFlag from "hooks/useFeatureFlag";
import { WorkspaceProvider } from "context/WorkspaceContext";
import RuleBuilder from "containers/Rulebuilder";
import RuleSetForm from "containers/Rulebuilder/RuleSetForm";
import EditRuleSet from "containers/Rulebuilder/EditRuleSet";
import ReleasePlan from "containers/ReleasePlan";
import ReleasePlanDetails from "containers/ReleasePlan/ReleasePlanDetails";
import { userHasAllowedGroup, userHasRuleBuilderAccess } from "utils/shared";
import DiagnosisGroups from "containers/DiagnosisGroups";
import ProcedureGroups from "containers/ProcedureGroups";
import CodingGroupEditor from "components/shared/CodingGroupComponents/CodingGroupEditor";
import PolicyTranslationPostOHR from "containers/PolicyTranslationPostOHR";
import { usePostOHR } from "context/PostOHRContext";
import { postUserActivity } from "components/shared/postUserActivity";
import Api from "api";

const LOGIN_ACTIVITY_TRACKED_KEY = "loginActivityTracked";
const LAST_LOGIN_TIME_KEY = "lastLoginTime";
function Container() {
  const [openIdleModal, setOpenIdleModal] = useState(false);
  const { logout, renewToken, authenticated } = useAuthentication();
  const [user] = useUser();
  const loginTrackingInFlightRef = useRef(false);
  const [lastLoginTime, setLastLoginTime] = useState(
    () => sessionStorage.getItem(LAST_LOGIN_TIME_KEY) || ""
  );

  const { postOHR, setPostOHR } = usePostOHR();
  const { loading } = useSelector((state) => state.app);

  const [healthcheckStatus, fetchHealthCheck] = useHealthcheck();
  const [featureFlags, fetchFlags] = useFeatureFlag();
  useEffect(() => {
    if (authenticated) fetchFlags();
  }, [authenticated]);
  useEffect(() => {
    if (authenticated) fetchHealthCheck();
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) {
      return;
    }

    if (loginTrackingInFlightRef.current) {
      return;
    }

    const loginActivityTracked =
      sessionStorage.getItem(LOGIN_ACTIVITY_TRACKED_KEY) === "true";
    const storedLastLoginTime =
      sessionStorage.getItem(LAST_LOGIN_TIME_KEY) || "";
    const hasLastLoginTime = Boolean(storedLastLoginTime);

    if (hasLastLoginTime) {
      setLastLoginTime(storedLastLoginTime);
    }

    if (loginActivityTracked && hasLastLoginTime) {
      return;
    }

    const trackLoginActivity = async () => {
      loginTrackingInFlightRef.current = true;
      let loginFetchSucceeded = false;
      let isFirstLogin = false;

      try {
        if (!hasLastLoginTime) {
          const loginActivityResponse =
            await Api.UserActivity.getLoginActivity();
          const previousLoginTime =
            loginActivityResponse?.data?.appCreateDatetime;

          if (
            typeof previousLoginTime === "string" &&
            previousLoginTime.trim()
          ) {
            sessionStorage.setItem(LAST_LOGIN_TIME_KEY, previousLoginTime);
            setLastLoginTime(previousLoginTime);
          } else {
            sessionStorage.removeItem(LAST_LOGIN_TIME_KEY);
            setLastLoginTime("");
          }
        }

        loginFetchSucceeded = true;
      } catch (error) {
        if (error?.response?.status === 404) {
          isFirstLogin = true;
        }
        console.error("Failed to get login activity:", error);
      }

      try {
        if (!loginActivityTracked && (loginFetchSucceeded || isFirstLogin)) {
          const response = await postUserActivity(
            null,
            null,
            200,
            "N/A",
            "click",
            "Navbar",
            "login",
            "login"
          );

          if (response?.status === 200) {
            sessionStorage.setItem(LOGIN_ACTIVITY_TRACKED_KEY, "true");
          }
        }
      } catch (error) {
        console.error("Failed to post login activity:", error);
      } finally {
        loginTrackingInFlightRef.current = false;
      }
    };

    trackLoginActivity();
  }, [authenticated, user]);

  const onPrompt = () => {
    setOpenIdleModal(true);
  };
  const onActive = () => {
    setOpenIdleModal(false);
  };
  const onIdle = () => {
    setOpenIdleModal(false);
    logout();
  };

  const { getRemainingTime, activate } = useIdleTimeout({
    onPrompt,
    idleTime: 15,
    onIdle,
    onActive,
  });

  const handleContinue = async () => {
    setOpenIdleModal(false);
    renewToken();
    activate();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // console.log(Math.max(getRemainingTime() - 60))
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime]);

  const accessToRuleBuilder = useCallback(
    () => userHasRuleBuilderAccess(user),
    [user]
  );
  // if (!user) return "Login user in...";

  return (
    <UserContext.Provider value={user}>
      <FeatureFlagContext.Provider value={featureFlags}>
        <WorkspaceProvider>
          <GenericDialog />
          {loading && <LoadingBackdrop />}
          <Navbar onLogout={logout} lastLoginTime={lastLoginTime} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden", // Prevent scrollbar on the main page
            }}
          >
            <Toolbar
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
              }}
            />
            <MuiContainer
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                mt: (theme) => theme.spacing(7),
                overflow: "auto",
                paddingLeft: { xs: 2, sm: 1.25 },
                paddingRight: { xs: 2, sm: 1.25 },
              }}
              maxWidth={false}
            >
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%", // Take full remaining space
                }}
              >
                {openIdleModal && (
                  <IdleModal
                    open={openIdleModal}
                    getRemainingTime={getRemainingTime}
                    handleContinue={handleContinue}
                    handleLogout={onIdle}
                  />
                )}
                <Routes>
                  <Route path="/login/callback" element={<LoginCallback />} />
                  <Route
                    path="/policies-active"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <PolicyEnforcementRulesView />{" "}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-history"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <PolicyHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-coding"
                    element={
                      <ProtectedRoute>
                        {" "}
                        {postOHR ? <PolicyCodingPostOHR /> : <PolicyCoding />}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-coding-post-ohr"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <PolicyCodingPostOHR />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-rules"
                    element={
                      <ProtectedRoute>
                        {" "}
                        {postOHR ? (
                          <PolicyTranslationPostOHR />
                        ) : (
                          <PolicyTranslation />
                        )}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-rules-post-ohr"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <PolicyTranslationPostOHR />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/hp-config"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <HealthPlanConfiguration />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/hp-config/edit/:id/:name"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <EditHealthPlanConfiguration />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-config"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <PolicyConfiguration />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-config/update/:policyKey"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <CreatePolicyConfig />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/policy-config/create"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <CreatePolicyConfig />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/use-cases"
                    element={
                      <ProtectedRoute>
                        {" "}
                        <UseCases />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/rule-builder"
                    element={
                      <ProtectedRoute hasAccess={accessToRuleBuilder}>
                        {" "}
                        <RuleBuilder />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/rule-builder/create-rulset"
                    element={
                      <ProtectedRoute hasAccess={accessToRuleBuilder}>
                        {" "}
                        <RuleSetForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/rule-builder/edit-ruleset/:ruleSetId"
                    element={
                      <ProtectedRoute hasAccess={accessToRuleBuilder}>
                        {" "}
                        <EditRuleSet />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/release-plan"
                    element={
                      <ProtectedRoute>
                        <ReleasePlan />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/release-plan/details/:releaseKey/:releaseNumber"
                    element={
                      <ProtectedRoute>
                        <ReleasePlanDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="/exclusions"
                    element={
                      <ProtectedRoute>
                        <Exclusions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/diagnosis-groups"
                    element={
                      <ProtectedRoute>
                        <DiagnosisGroups />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/procedure-groups"
                    element={
                      <ProtectedRoute>
                        <ProcedureGroups />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coding-group-editor"
                    element={
                      <ProtectedRoute>
                        <CodingGroupEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coding-group-editor/:codeGroupKey"
                    element={
                      <ProtectedRoute>
                        <CodingGroupEditor />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Box>
            </MuiContainer>
            <StyledFooter />
          </Box>
        </WorkspaceProvider>
      </FeatureFlagContext.Provider>
    </UserContext.Provider>
  );
}

export default Container;
